import type { BranchCreatePayload } from "./client";
import type { ScheduleUpdatePayload } from "@/modules/branches/domain/types";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { branchesApi } from "./client";

export const branchesKeys = {
  all: ["branches"] as const,
  list: (q: Partial<OffsetPageQueryDTO>) => ["branches", "list", q] as const,
  detail: (id: string) => ["branches", "detail", id] as const,
  pickupSlots: (id: string) => ["branches", "pickup-slots", id] as const,
};

export function useBranchesQuery(query: Partial<OffsetPageQueryDTO> = {}) {
  const q: OffsetPageQueryDTO = {
    limit: query.limit ?? 20,
    offset: query.offset ?? 0,
    sort: query.sort,
    filters: query.filters,
  };
  return useQuery({
    queryKey: branchesKeys.list(q),
    queryFn: () => branchesApi.list(q),
  });
}

export function useBranchQuery(id: string) {
  return useQuery({
    queryKey: branchesKeys.detail(id),
    queryFn: () => branchesApi.get(id),
    enabled: !!id,
  });
}

export function useBranchWithSettingsQuery(id: string) {
  return useQuery({
    queryKey: [...branchesKeys.detail(id), "with-settings"] as const,
    queryFn: () => branchesApi.getWithSettings(id),
    enabled: !!id,
  });
}

export function useUpdateBranchSchedule(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ScheduleUpdatePayload) =>
      branchesApi.updateSchedule(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: branchesKeys.detail(id) });
      qc.invalidateQueries({ queryKey: branchesKeys.all });
      qc.invalidateQueries({ queryKey: branchesKeys.pickupSlots(id) });
    },
  });
}

export function usePickupSlotsQuery(branchId: string, date: string) {
  return useQuery({
    queryKey: [...branchesKeys.pickupSlots(branchId), date] as const,
    queryFn: () => branchesApi.getPickupSlots(branchId, date),
    enabled: !!branchId && /^\d{4}-\d{2}-\d{2}$/.test(date),
  });
}

export function useCreateBranch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: BranchCreatePayload) => branchesApi.create(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: branchesKeys.all });
    },
  });
}

export function useUpdateBranch(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: BranchCreatePayload) =>
      branchesApi.update(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: branchesKeys.detail(id) });
      qc.invalidateQueries({ queryKey: branchesKeys.all });
    },
  });
}

export function useDeleteBranch() {
  const qc = useQueryClient();
  const base = useMutation({
    mutationFn: (id: string) => branchesApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: branchesKeys.all });
    },
  });

  const run = (id: string) =>
    new Promise<void>((resolve, reject) => {
      base.mutate(id, {
        onSuccess: () => resolve(),
        onError: (e) => reject(e),
      });
    });

  return { ...base, run };
}
